import type { FeatureFlagDefinitionEntryInput } from './feature-flags.constants';
import {
  COPILOT_DEFERRED_TOOL_LOADING_ENABLED_FLAG,
  COPILOT_DOCS_SEARCH_TOOL_ENABLED_FLAG,
  COPILOT_EXTENDED_PROMPT_CACHE_TTL_ENABLED_FLAG,
  COPILOT_TOOL_RESULT_BUDGET_ENABLED_FLAG,
  COPILOT_ACTION_AUTO_REVIEW_ENABLED_FLAG,
  COPILOT_MODEL_RESILIENCE_ENABLED_FLAG,
  COPILOT_ACTION_APPROVAL_RULES_ENABLED_FLAG,
  COPILOT_SYSTEM_REMINDERS_ENABLED_FLAG,
  DYNAMIC_APPLICATION_SCAN_AUTOMATION_ENABLED_FLAG,
  NOVA_SCAN_PRIORITY_FLAG,
  DEFAULT_NOVA_SCAN_PRIORITY,
  COMPOUNDING_PATH_MIN_REPEATS_FLAG,
  ENDPOINT_DISCOVERY_CLAUDE_BG_WAIT_CEILING_MS_FLAG,
  DEFAULT_ENDPOINT_DISCOVERY_CLAUDE_BG_WAIT_CEILING_MS,
  CLAUDE_SESSION_S3_STORE_ENABLED_FLAG,
  MCP_SERVERS_ENABLED_FLAG,
  COPY_WEB_APPLICATION_TO_AI_ENABLED_FLAG,
  UNIFIED_WEB_AI_APPLICATIONS_ENABLED_FLAG,
  NUCLEI_TEMPLATE_TOOLS_ENABLED_FLAG,
  CODE_CHANGES_PROCESSED_PR_LIST_ENABLED_FLAG,
  REPOSITORY_RELEVANCE_CATALOG_ENABLED,
  REPOSITORY_EXPANSION_CAP_FLAG,
  DEFAULT_REPOSITORY_EXPANSION_CAP,
  REPOSITORY_CATALOG_CLONE_ALL_FALLBACK_ENABLED,
  REPOSITORY_CATALOG_MIN_CONNECTED_REPOSITORIES_FLAG,
  DEFAULT_REPOSITORY_CATALOG_MIN_CONNECTED_REPOSITORIES,
  SOURCE_CODE_UPLOAD_MAX_FILES_PER_BATCH_FLAG,
  DEFAULT_SOURCE_CODE_UPLOAD_MAX_FILES_PER_BATCH,
  INGEST_TARGET_BRANCH_COMMITS_AS_PRS_ENABLED,
  IP_ALLOWLIST_ENABLED_FLAG,
  AUTHENTICATOR_RECURSION_LIMIT_FLAG,
  DEFAULT_AUTHENTICATOR_RECURSION_LIMIT,
  CVSS_CODEBASE_TOOL_ENABLED_FLAG,
  FINDING_CVSS_DETAILS_TAB_ENABLED_FLAG,
  OWNED_MEMORY_DUAL_WRITE_ENABLED_FLAG,
  OWNED_MEMORY_READS_ENABLED_FLAG,
} from './feature-flags.constants';

export const PRODUCT_FEATURE_FLAG_DEFINITIONS = [
  {
    name: 'CODER_PRESERVE_RAW_URL_PATHS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'CUSTOMER_SKILLS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'COPILOT_DOCS_INGESTION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: COPILOT_DOCS_SEARCH_TOOL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'COPILOT_DEV_TOOLS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: COPILOT_EXTENDED_PROMPT_CACHE_TTL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: COPILOT_DEFERRED_TOOL_LOADING_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: COPILOT_TOOL_RESULT_BUDGET_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: COPILOT_ACTION_AUTO_REVIEW_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: COPILOT_MODEL_RESILIENCE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: COPILOT_ACTION_APPROVAL_RULES_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: COPILOT_SYSTEM_REMINDERS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'COPILOT_EXPLICIT_ERRORS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: MCP_SERVERS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: COPY_WEB_APPLICATION_TO_AI_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: UNIFIED_WEB_AI_APPLICATIONS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: NUCLEI_TEMPLATE_TOOLS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: CODE_CHANGES_PROCESSED_PR_LIST_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: REPOSITORY_RELEVANCE_CATALOG_ENABLED,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: REPOSITORY_EXPANSION_CAP_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_REPOSITORY_EXPANSION_CAP,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: REPOSITORY_CATALOG_CLONE_ALL_FALLBACK_ENABLED,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: REPOSITORY_CATALOG_MIN_CONNECTED_REPOSITORIES_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_REPOSITORY_CATALOG_MIN_CONNECTED_REPOSITORIES,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: SOURCE_CODE_UPLOAD_MAX_FILES_PER_BATCH_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_SOURCE_CODE_UPLOAD_MAX_FILES_PER_BATCH,
    supportedTargetTypes: ['tenant'] as const,
  },
  {
    name: INGEST_TARGET_BRANCH_COMMITS_AS_PRS_ENABLED,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['application'] as const,
  },
  {
    name: 'FINDING_VULNERABILITY_CHAINING_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'EXTERNAL_NETWORK_FINDING_CHAINING_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant'] as const,
  },
  {
    name: 'FINDING_CHAINING_INCLUDE_DRAFT_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'REVERSE_WHOIS_SEED_EXPANSION_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'RETEST_STATUS_UPDATE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'FINDINGS_RETEST_FILTER_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant'] as const,
  },
  {
    name: 'SEVERITY_CHANGE_REQUEST_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'MCP_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'MCP_SCANNER_TOOLS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'INTERNAL_NETWORK_AGENT_SCENARIO_GEN_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'SSO_SELF_SERVICE_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: 'BOOTH_TOUR_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: DYNAMIC_APPLICATION_SCAN_AUTOMATION_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: NOVA_SCAN_PRIORITY_FLAG,
    type: 'STRING' as const,
    defaultValue: DEFAULT_NOVA_SCAN_PRIORITY,
    supportedTargetTypes: ['tenant'] as const,
  },
  {
    name: 'TERRA_ACADEMY_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'PDF_REPORT_WHITE_LABEL_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: 'FINDING_AUDIT_HIDE_STAFF_ACTIONS_ENABLED',
    type: 'BOOLEAN' as const,
    defaultValue: true,
    supportedTargetTypes: ['tenant'] as const,
  },
  {
    name: IP_ALLOWLIST_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
  {
    name: CVSS_CODEBASE_TOOL_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: FINDING_CVSS_DETAILS_TAB_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant'] as const,
  },
  {
    name: COMPOUNDING_PATH_MIN_REPEATS_FLAG,
    type: 'NUMBER' as const,
    defaultValue: 3,
  },
  {
    name: ENDPOINT_DISCOVERY_CLAUDE_BG_WAIT_CEILING_MS_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_ENDPOINT_DISCOVERY_CLAUDE_BG_WAIT_CEILING_MS,
  },
  {
    name: CLAUDE_SESSION_S3_STORE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: AUTHENTICATOR_RECURSION_LIMIT_FLAG,
    type: 'NUMBER' as const,
    defaultValue: DEFAULT_AUTHENTICATOR_RECURSION_LIMIT,
    supportedTargetTypes: ['tenant', 'application'] as const,
  },
  {
    name: OWNED_MEMORY_DUAL_WRITE_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: true,
  },
  {
    name: OWNED_MEMORY_READS_ENABLED_FLAG,
    type: 'BOOLEAN' as const,
    defaultValue: false,
  },
] as const satisfies readonly FeatureFlagDefinitionEntryInput[];
